// common
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "@/store";

//helpers
import {
  IPrinterGetResponse,
  IPrinterUsage,
  IPrinterConfigState,
} from "@/interfaces/printer/printer";
import { TicketTypes } from "@/interfaces/receipts";
import POSConfigModule from "./pos-config";
import { isBelgianCompliant } from "@/utils/vue-helpers/compliance";

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "PrinterConfiguration",
})
class PrinterConfiguration extends VuexModule {
  public printers: IPrinterGetResponse[] = [];
  public printerOption: IPrinterUsage = {
    auto: false,
    printer: null,
  };
  public ticketType: TicketTypes = TicketTypes.Ticket;
  public labelPrinter: string | null = null;
  public ticketPrinter: string | null = null;
  public receiptPrinter: string | null = null;
  public isLabelPrintingEnabled = false;
  public isTicketPrintingEnabled = true;

  public autoPrintReceipt = false;
  public isOrderSummaryReceiptEnabled = false;
  public orderSummaryReceiptPrinter: string | null = null;

  /**
   * Belgium prints a paper receipt for every sale, which is also what the FDM
   * is told when the sale is signed, so the operator has nothing to choose and
   * the setting is hidden.
   */
  public get isAutoPrintReceiptForced(): boolean {
    return isBelgianCompliant.value;
  }

  /**
   * Whether the receipt prints automatically after a sale: forced in Belgium,
   * elsewhere it follows the operator's saved preference.
   */
  public get isAutoPrintReceiptEnabled(): boolean {
    return this.isAutoPrintReceiptForced || this.autoPrintReceipt;
  }

  public get isNativeTicketPrintingEnabled(): boolean {
    return (
      POSConfigModule.isVHS ||
      POSConfigModule.isEdeis ||
      POSConfigModule.groupId === 19579 // Paul Ricard
    );
  }

  public get getTicketType() {
    return this.ticketType;
  }

  public get hasAutoPrinting(): boolean {
    if (this.printerOption.auto) {
      return true;
    }
    return false;
  }

  public get selectedPrinter(): IPrinterGetResponse | null {
    const printerOption = this.printerOption;
    if (printerOption && printerOption.printer) {
      // find the printer
      const chosenPrinter = this.printers.find(
        (p) => printerOption.printer === p.printer_id
      );

      // set the printer
      if (chosenPrinter) {
        return chosenPrinter;
      }
    }
    return null;
  }

  // get the selected printer id
  public get selectedPrinterId(): number | null {
    const printerOption = this.printerOption;
    if (printerOption && printerOption.printer) {
      return printerOption.printer;
    }
    return null;
  }

  @Mutation
  public setPrinters(printers: IPrinterGetResponse[]) {
    this.printers = printers;
  }

  @Mutation
  public setPrinterOption(printerOption: {
    auto: boolean;
    printer: number | null;
  }) {
    this.printerOption = printerOption;
  }

  @Mutation
  restore(moduleFields: Partial<IPrinterConfigState>) {
    const {
      printerOption,
      ticketType,
      labelPrinter,
      ticketPrinter,
      isLabelPrintingEnabled,
      isTicketPrintingEnabled,
      autoPrintReceipt,
      orderSummaryReceiptPrinter,
      isOrderSummaryReceiptEnabled,
      receiptPrinter,
    } = moduleFields;
    if (printerOption) {
      this.printerOption = printerOption;
    }
    if (ticketType) {
      this.ticketType = ticketType;
    }
    if (labelPrinter) {
      this.labelPrinter = labelPrinter;
    }
    if (ticketPrinter) {
      this.ticketPrinter = ticketPrinter;
    }
    if (isLabelPrintingEnabled) {
      this.isLabelPrintingEnabled = isLabelPrintingEnabled;
    }
    if (isTicketPrintingEnabled != null) {
      this.isTicketPrintingEnabled = isTicketPrintingEnabled;
    }
    if (receiptPrinter) {
      this.receiptPrinter = receiptPrinter;
    }
    this.autoPrintReceipt = autoPrintReceipt || false;
    this.orderSummaryReceiptPrinter = orderSummaryReceiptPrinter || null;
    this.isOrderSummaryReceiptEnabled = isOrderSummaryReceiptEnabled || false;
  }

  @Mutation
  public setTicketType(type: TicketTypes) {
    this.ticketType = type;
  }
  @Mutation
  public setLabelPrinter(printer: string) {
    this.labelPrinter = printer;
  }
  @Mutation
  public setTicketPrinter(printer: string) {
    this.ticketPrinter = printer;
  }
  @Mutation
  public setReceiptPrinter(printer: string) {
    this.receiptPrinter = printer;
  }
  @Mutation
  public setIsLabelPrintingEnabled(status: boolean) {
    this.isLabelPrintingEnabled = status;
  }
  @Mutation
  public setIsTicketPrintingEnabled(status: boolean) {
    this.isTicketPrintingEnabled = status;
  }
  @Mutation
  public setAutoPrintReceipt(status: boolean) {
    this.autoPrintReceipt = status;
  }

  @Mutation
  public setOrderSummaryReceiptPrinter(printer: string) {
    this.orderSummaryReceiptPrinter = printer;
  }

  @Mutation
  public setIsOrderSummaryReceiptStatus(status: boolean) {
    this.isOrderSummaryReceiptEnabled = status;
  }
}
const PrinterConfigModule = getModule(PrinterConfiguration);
export default PrinterConfigModule;
